// ============================================================
// EMAIL EDITOR — TipTap-powered rich text editor for email
// ============================================================

// CDN imports from esm.sh (TipTap v2)
const TIPTAP_CDN = 'https://esm.sh';
let Editor, Extension, StarterKit, Underline, Link, Placeholder;
let TextStyle, FontFamily, Color, FontSize, LineHeight;
let _editorLoaded = false;
let _loadPromise = null;

// ── Styled link modal (replaces browser prompt) ─────────────
function _showLinkModal(defaultUrl, onConfirm) {
  const overlay = document.createElement('div');
  overlay.className = 'ee-link-overlay';
  overlay.innerHTML = `
    <div class="ee-link-modal">
      <div class="ee-link-title">Insert Link</div>
      <input type="url" class="ee-link-input" id="ee-link-url" value="${(defaultUrl || '').replace(/"/g, '&quot;')}" placeholder="https://example.com" spellcheck="false" />
      <div class="ee-link-actions">
        <button class="ee-link-btn ee-link-btn--cancel" id="ee-link-cancel">Cancel</button>
        <button class="ee-link-btn ee-link-btn--remove" id="ee-link-remove">Remove Link</button>
        <button class="ee-link-btn ee-link-btn--ok" id="ee-link-ok">Apply</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const input = document.getElementById('ee-link-url');
  input.focus();
  input.select();

  const close = () => overlay.remove();

  document.getElementById('ee-link-cancel').onclick = close;
  overlay.onclick = (e) => { if (e.target === overlay) close(); };
  document.getElementById('ee-link-remove').onclick = () => { onConfirm(''); close(); };
  document.getElementById('ee-link-ok').onclick = () => {
    const val = input.value.trim();
    if (val && val !== 'https://') onConfirm(val);
    close();
  };
  input.onkeydown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); document.getElementById('ee-link-ok').click(); }
    if (e.key === 'Escape') close();
  };
}

// ── Load TipTap from CDN ──────────────────────────────────
async function loadTipTap() {
  if (_editorLoaded) return;
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    try {
      const [core, sk, ul, lk, ph, ts, ff, col] = await Promise.all([
        import(`${TIPTAP_CDN}/@tiptap/core@2.6.6`),
        import(`${TIPTAP_CDN}/@tiptap/starter-kit@2.6.6`),
        import(`${TIPTAP_CDN}/@tiptap/extension-underline@2.6.6`),
        import(`${TIPTAP_CDN}/@tiptap/extension-link@2.6.6`),
        import(`${TIPTAP_CDN}/@tiptap/extension-placeholder@2.6.6`),
        import(`${TIPTAP_CDN}/@tiptap/extension-text-style@2.6.6`),
        import(`${TIPTAP_CDN}/@tiptap/extension-font-family@2.6.6`),
        import(`${TIPTAP_CDN}/@tiptap/extension-color@2.6.6`),
      ]);
      Editor = core.Editor;
      Extension = core.Extension;
      StarterKit = sk.default || sk.StarterKit;
      Underline = ul.default || ul.Underline;
      Link = lk.default || lk.Link;
      Placeholder = ph.default || ph.Placeholder;
      TextStyle = ts.default || ts.TextStyle;
      FontFamily = ff.default || ff.FontFamily;
      Color = col.default || col.Color;

      // Custom extensions — no official equivalents in TipTap v2.6.6. Both emit
      // plain inline styles the email renderer already understands and inlines:
      // font-size on the textStyle <span>, line-height on the <p>/<h*> block.
      FontSize = Extension.create({
        name: 'fontSize',
        addGlobalAttributes() {
          return [{
            types: ['textStyle'],
            attributes: {
              fontSize: {
                default: null,
                parseHTML: (el) => el.style.fontSize || null,
                renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
              },
            },
          }];
        },
        addCommands() {
          return {
            setFontSize: (size) => ({ chain }) => chain().setMark('textStyle', { fontSize: size }).run(),
            unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
          };
        },
      });
      LineHeight = Extension.create({
        name: 'lineHeight',
        addGlobalAttributes() {
          return [{
            types: ['paragraph', 'heading'],
            attributes: {
              lineHeight: {
                default: null,
                parseHTML: (el) => el.style.lineHeight || null,
                renderHTML: (attrs) => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
              },
            },
          }];
        },
        addCommands() {
          return {
            setLineHeight: (lh) => ({ commands }) => ['paragraph', 'heading'].every((t) => commands.updateAttributes(t, { lineHeight: lh })),
            unsetLineHeight: () => ({ commands }) => ['paragraph', 'heading'].every((t) => commands.resetAttributes(t, 'lineHeight')),
          };
        },
      });

      _editorLoaded = true;
      console.log('[email-editor] TipTap loaded');
    } catch (err) {
      console.error('[email-editor] Failed to load TipTap:', err);
      _loadPromise = null;
    }
  })();
  return _loadPromise;
}

// ── Toolbar controls ──────────────────────────────────────
// Curated, email-safe font stacks (each renders well across clients; custom
// web fonts are intentionally excluded — Outlook would fall back to Times).
const FONT_FAMILIES = [
  ['', 'Font'],
  ['Arial, Helvetica, sans-serif', 'Arial'],
  ['Helvetica, Arial, sans-serif', 'Helvetica'],
  ['Verdana, Geneva, sans-serif', 'Verdana'],
  ['Tahoma, Geneva, sans-serif', 'Tahoma'],
  ["'Trebuchet MS', Helvetica, sans-serif", 'Trebuchet'],
  ["Georgia, 'Times New Roman', serif", 'Georgia'],
  ["'Times New Roman', Times, serif", 'Times'],
  ["'Courier New', Courier, monospace", 'Courier'],
];
const FONT_SIZES = [['', 'Size'], ['12px', '12'], ['13px', '13'], ['14px', '14'], ['16px', '16'], ['18px', '18'], ['20px', '20'], ['24px', '24'], ['28px', '28']];
const LINE_HEIGHTS = [['', 'Spacing'], ['1.2', 'Tight'], ['1.5', 'Snug'], ['1.6', 'Normal'], ['1.8', 'Relaxed'], ['2.2', 'Loose']];

function _opts(pairs) {
  return pairs.map(([v, l]) => `<option value="${v}">${l}</option>`).join('');
}

// ── Toolbar HTML ──────────────────────────────────────────
// `simpleToolbar` (S67, program overview): no font family / size / line
// spacing / color controls. Those exist for EMAIL, where the coach owns the
// whole canvas; overview HTML renders inside the client app's own type
// system, where a hand-picked font or a dark text color on the dark canvas
// would break the design (and the dropdowns never reflected a selection in
// that modal anyway). Structure-only formatting stays.
function buildToolbarHTML(editorId, opts = {}) {
  const typography = opts.simpleToolbar ? '' : `
    <select class="ee-tb-select" data-ctl="fontFamily" title="Font family">${_opts(FONT_FAMILIES)}</select>
    <select class="ee-tb-select ee-tb-select--sm" data-ctl="fontSize" title="Font size">${_opts(FONT_SIZES)}</select>
    <select class="ee-tb-select ee-tb-select--sm" data-ctl="lineHeight" title="Line spacing">${_opts(LINE_HEIGHTS)}</select>
    <label class="ee-tb-color" title="Text color"><input type="color" data-ctl="color" value="#1a1d28" /></label>
    <span class="ee-tb-sep"></span>`;
  return `<div class="ee-toolbar" id="eet-${editorId}">
    <button class="ee-tb-btn" data-cmd="bold" title="Bold"><strong>B</strong></button>
    <button class="ee-tb-btn" data-cmd="italic" title="Italic"><em>I</em></button>
    <button class="ee-tb-btn" data-cmd="underline" title="Underline"><u>U</u></button>
    <button class="ee-tb-btn" data-cmd="strike" title="Strikethrough"><s>S</s></button>
    <span class="ee-tb-sep"></span>
    ${typography}
    <button class="ee-tb-btn" data-cmd="bulletList" title="Bullet List">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1" fill="currentColor"/><circle cx="3" cy="12" r="1" fill="currentColor"/><circle cx="3" cy="18" r="1" fill="currentColor"/></svg>
    </button>
    <button class="ee-tb-btn" data-cmd="orderedList" title="Ordered List">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><text x="1" y="8" font-size="8" fill="currentColor" stroke="none">1</text><text x="1" y="14" font-size="8" fill="currentColor" stroke="none">2</text><text x="1" y="20" font-size="8" fill="currentColor" stroke="none">3</text></svg>
    </button>
    <span class="ee-tb-sep"></span>
    <button class="ee-tb-btn" data-cmd="link" title="Insert Link">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
    </button>
    <button class="ee-tb-btn" data-cmd="emoji" title="Emoji">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
    </button>
    <span class="ee-tb-sep"></span>
    <button class="ee-tb-btn" data-cmd="clearBody" title="Clear message body">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
    </button>
  </div>`;
}

// ── Emoji picker HTML ─────────────────────────────────────
const COMMON_EMOJIS = ['😀','😂','😊','🙂','😍','🤩','😎','🤔','😅','😢','😤','🙏','👍','👎','👏','🔥','💪','❤️','✅','⭐','🎯','📌','📎','📧','💬','🏋️','🥇','💯','🚀','⚡'];

function buildEmojiPickerHTML(editorId) {
  const emojis = COMMON_EMOJIS.map(e => `<button class="ee-emoji-btn" data-emoji="${e}">${e}</button>`).join('');
  return `<div class="ee-emoji-picker hidden" id="eep-${editorId}">
    <div class="ee-emoji-grid">${emojis}</div>
  </div>`;
}

// ── Create editor instance ────────────────────────────────
// Returns { editor, element, getHTML, destroy }
export async function createEmailEditor(containerId, opts = {}) {
  await loadTipTap();
  if (!_editorLoaded) {
    console.warn('[email-editor] TipTap not available, falling back to textarea');
    return null;
  }

  const container = document.getElementById(containerId);
  if (!container) return null;

  const editorId = containerId;
  const placeholder = opts.placeholder || 'Type your email...';

  // Build HTML structure
  container.innerHTML = `
    <div class="ee-editor-wrap" id="eew-${editorId}">
      <div class="ee-content" id="eec-${editorId}"></div>
      ${buildToolbarHTML(editorId, opts)}
      ${buildEmojiPickerHTML(editorId)}
    </div>`;

  // Create TipTap instance
  const editor = new Editor({
    element: document.getElementById('eec-' + editorId),
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener' } }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      FontFamily,
      Color.configure({ types: ['textStyle'] }),
      FontSize,
      LineHeight,
    ],
    content: opts.content || '',
    editorProps: {
      attributes: { class: 'ee-prosemirror' },
    },
  });

  // Wire toolbar
  const toolbar = document.getElementById('eet-' + editorId);
  toolbar?.addEventListener('click', (e) => {
    const btn = e.target.closest('.ee-tb-btn');
    if (!btn) return;
    e.preventDefault();
    const cmd = btn.dataset.cmd;

    switch (cmd) {
      case 'bold': editor.chain().focus().toggleBold().run(); break;
      case 'italic': editor.chain().focus().toggleItalic().run(); break;
      case 'underline': editor.chain().focus().toggleUnderline().run(); break;
      case 'strike': editor.chain().focus().toggleStrike().run(); break;
      case 'bulletList': editor.chain().focus().toggleBulletList().run(); break;
      case 'orderedList': editor.chain().focus().toggleOrderedList().run(); break;
      case 'link': {
        const prev = editor.getAttributes('link').href || '';
        _showLinkModal(prev || 'https://', (url) => {
          if (url === '') { editor.chain().focus().unsetLink().run(); return; }
          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        });
        break;
      }
      case 'emoji': {
        const picker = document.getElementById('eep-' + editorId);
        picker?.classList.toggle('hidden');
        break;
      }
      case 'clearBody': {
        editor.commands.clearContent();
        break;
      }
    }

    // Update active states
    toolbar.querySelectorAll('.ee-tb-btn[data-cmd]').forEach(b => {
      const c = b.dataset.cmd;
      if (['bold','italic','underline','strike','bulletList','orderedList'].includes(c)) {
        b.classList.toggle('active', editor.isActive(c));
      }
    });
  });

  // Font / size / line-spacing / color selects → editor commands.
  toolbar?.addEventListener('change', (e) => {
    const ctl = e.target.dataset?.ctl;
    if (!ctl) return;
    const v = e.target.value;
    const chain = editor.chain().focus();
    if (ctl === 'fontFamily') { v ? chain.setFontFamily(v).run() : chain.unsetFontFamily().run(); }
    else if (ctl === 'fontSize') { v ? chain.setFontSize(v).run() : chain.unsetFontSize().run(); }
    else if (ctl === 'lineHeight') { v ? chain.setLineHeight(v).run() : chain.unsetLineHeight().run(); }
    else if (ctl === 'color') { chain.setColor(v).run(); }
  });

  // Reflect the formatting at the cursor back into the toolbar controls.
  function syncControls() {
    if (!toolbar) return;
    const ts = editor.getAttributes('textStyle') || {};
    const lh = (editor.getAttributes('paragraph') || {}).lineHeight
            || (editor.getAttributes('heading') || {}).lineHeight || '';
    const setVal = (ctl, val) => { const el = toolbar.querySelector(`[data-ctl="${ctl}"]`); if (el && el.value !== (val || '')) el.value = val || ''; };
    setVal('fontFamily', ts.fontFamily);
    setVal('fontSize', ts.fontSize);
    setVal('lineHeight', lh);
    if (ts.color) { const c = toolbar.querySelector('[data-ctl="color"]'); if (c) c.value = ts.color; }
  }

  // Update active states + control values on selection change
  editor.on('selectionUpdate', () => {
    toolbar?.querySelectorAll('.ee-tb-btn[data-cmd]').forEach(b => {
      const c = b.dataset.cmd;
      if (['bold','italic','underline','strike','bulletList','orderedList'].includes(c)) {
        b.classList.toggle('active', editor.isActive(c));
      }
    });
    syncControls();
  });

  // Emoji picker clicks
  const emojiPicker = document.getElementById('eep-' + editorId);
  emojiPicker?.addEventListener('click', (e) => {
    const btn = e.target.closest('.ee-emoji-btn');
    if (!btn) return;
    editor.chain().focus().insertContent(btn.dataset.emoji).run();
    emojiPicker.classList.add('hidden');
  });

  // Close emoji picker when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.ee-emoji-picker') && !e.target.closest('[data-cmd="emoji"]')) {
      emojiPicker?.classList.add('hidden');
    }
  });

  return {
    editor,
    getHTML: () => editor.getHTML(),
    getText: () => editor.getText(),
    setContent: (html) => editor.commands.setContent(html),
    clear: () => editor.commands.clearContent(),
    focus: () => editor.commands.focus('start'),
    destroy: () => editor.destroy(),
  };
}

// ── CSS injection ─────────────────────────────────────────
// Inject editor styles once
let _cssInjected = false;
export function injectEditorCSS() {
  if (_cssInjected) return;
  _cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .ee-editor-wrap {
      display: flex;
      flex-direction: column;
      border-top: 1px solid var(--border);
      position: relative;
    }
    .ee-content {
      flex: 1;
      overflow-y: auto;
      min-height: 100px;
      max-height: 250px;
    }
    .ee-prosemirror {
      padding: 12px 14px;
      font-size: 13px;
      color: var(--text);
      line-height: 1.6;
      outline: none;
      min-height: 80px;
    }
    .ee-prosemirror p { margin: 0 0 10px; }
    .ee-prosemirror p:last-child { margin-bottom: 0; }
    .ee-prosemirror ul, .ee-prosemirror ol { padding-left: 22px; margin: 8px 0 12px; }
    .ee-prosemirror li { margin: 0 0 6px; }
    .ee-prosemirror li:last-child { margin-bottom: 0; }
    .ee-prosemirror li p { margin: 0; }
    .ee-prosemirror a { color: var(--accent); text-decoration: underline; }
    .ee-prosemirror p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: var(--text-muted);
      float: left;
      height: 0;
      pointer-events: none;
    }
    /* Toolbar */
    .ee-toolbar {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 6px 10px;
      border-top: 1px solid var(--border);
      background: var(--bg-hover);
      flex-wrap: wrap;
    }
    .ee-tb-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 4px;
      border: none;
      background: transparent;
      color: var(--text-muted);
      cursor: pointer;
      font-size: 13px;
      padding: 0;
    }
    .ee-tb-btn:hover { background: var(--surface-2); color: var(--text); }
    .ee-tb-btn.active { background: rgba(139,92,246,0.15); color: var(--accent); }
    .ee-tb-sep {
      width: 1px;
      height: 18px;
      background: var(--border);
      margin: 0 4px;
    }
    /* Font family / size / line-height dropdowns + colour picker */
    .ee-tb-select {
      height: 28px;
      border-radius: 4px;
      border: 1px solid var(--border);
      background: var(--bg-hover);
      color: var(--text);
      font-size: 12px;
      padding: 0 4px;
      cursor: pointer;
      max-width: 104px;
    }
    .ee-tb-select:hover { border-color: var(--accent); }
    .ee-tb-select--sm { max-width: 76px; }
    .ee-tb-color {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 4px;
      overflow: hidden;
      cursor: pointer;
    }
    .ee-tb-color:hover { background: var(--surface-2); }
    .ee-tb-color input[type="color"] {
      width: 36px;
      height: 36px;
      margin: -4px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }
    /* Emoji picker */
    .ee-emoji-picker {
      position: absolute;
      bottom: 44px;
      left: 10px;
      background: var(--surface, var(--bg-card, #1e1e2e));
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 12px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.4);
      z-index: 100;
      width: fit-content;
      backdrop-filter: blur(12px);
    }
    .ee-emoji-grid {
      display: grid;
      grid-template-columns: repeat(10, 28px);
      gap: 2px;
    }
    .ee-emoji-btn {
      width: 28px;
      height: 28px;
      border: none;
      background: transparent;
      border-radius: 4px;
      cursor: pointer;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }
    .ee-emoji-btn:hover { background: var(--bg-hover); }

    /* Link modal */
    .ee-link-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.5);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ee-link-modal {
      background: var(--surface, #1e1e2e);
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 20px;
      width: 380px;
      max-width: 90vw;
      box-shadow: 0 20px 60px rgba(0,0,0,0.4);
    }
    .ee-link-title {
      font-size: 14px;
      font-weight: 700;
      color: var(--text);
      margin-bottom: 12px;
    }
    .ee-link-input {
      width: 100%;
      padding: 10px 12px;
      font-size: 13px;
      background: var(--bg-hover, #2a2a3e);
      border: 1px solid var(--border);
      border-radius: 6px;
      color: var(--text);
      outline: none;
      box-sizing: border-box;
    }
    .ee-link-input:focus { border-color: var(--primary); }
    .ee-link-actions {
      display: flex;
      gap: 8px;
      margin-top: 14px;
      justify-content: flex-end;
    }
    .ee-link-btn {
      padding: 8px 14px;
      font-size: 12px;
      font-weight: 600;
      border-radius: 6px;
      border: none;
      cursor: pointer;
    }
    .ee-link-btn--cancel {
      background: transparent;
      color: var(--text-muted);
    }
    .ee-link-btn--cancel:hover { color: var(--text); }
    .ee-link-btn--remove {
      background: transparent;
      color: var(--danger, #ef4444);
    }
    .ee-link-btn--remove:hover { opacity: 0.8; }
    .ee-link-btn--ok {
      background: var(--primary);
      color: #fff;
    }
    .ee-link-btn--ok:hover { opacity: 0.9; }
  `;
  document.head.appendChild(style);
}
